import { AddressModel } from "./data.address.model";
import { CriminalRecordModel } from "./data.criminalRecord.model";
import { SafeInEducationModel } from "./data.safeInEducation.model";
import { HealthRecordModel, IHealthRecord } from "./data.healthRecord.model";
import { NextOfKinModel } from "./data.nextOfKin.model";
import { BankDetailsModel } from "./data.bankDetails.model";
import { CandidateErrorInterface, CandidateInterface, CandidateSignatures, PreviousAgencyInterface } from "../_types/candidate";
import { UserInterface } from "../_types/user";
import { OccupationTypes } from "../_enums/occipationTypes.enum";
import { CandidatePay, CandidatePaymentIntervals } from "../_enums/candidatePay.enum";
import { SocialMediaModel } from "./data.socialMedia.model";
import { IPreviousAddress } from "./data.previousAddress.model";
import { CandidateSignaturesModel } from "./data.candidateSignatures.model";
import type { HealthDeclarationSnapshot, HealthDeclarationSubmission } from "../_types/healthDeclaration";
import type { CriminalRecordDeclarationSnapshot, CriminalRecordDeclarationSubmission } from "../_types/criminalRecordDeclaration";

export class CandidateModel {
	forename: string;
	surname: string;
	previousNames?: string;
	email: string;
	occupation?: OccupationTypes;
	trnNumber?: string;
	trnNumberCheckedDate?: string;
	trnNumberNotes?: string;
	employmentStartDate?: string;
	ewcConfirmation?: boolean;
	homeOfficeShareCode?: string;
	subject?: string;
	telephone?: string;
	mobile?: string;
	address: AddressModel;
	qualifications: any;
	employment: any;
	references: any;
	previousAgencies: PreviousAgencyInterface[];
	previousAddresses: IPreviousAddress[];
	dob?: string;
	sex?: string;
	status: number;
	onboardingSent?: boolean;
	nextOfKin: NextOfKinModel;
	criminalRecord: CriminalRecordModel;
	criminalRecordDeclaration?: CriminalRecordDeclarationSnapshot | CriminalRecordDeclarationSubmission;
	safeInEducation: SafeInEducationModel;
	socialMedia: SocialMediaModel;
	healthRecord: IHealthRecord;
	healthDeclaration?: HealthDeclarationSnapshot | HealthDeclarationSubmission;
	bankDetails: BankDetailsModel;
	referenceCheckConsent: boolean;
	referenceCheckConsentName?: string;
	referenceCheckConsentSignature?: string;
	referenceCheckConsentDate?: string;
	safeInEducationCheckConsent: boolean;
	safeInEducationCheckConsentName?: string;
	safeInEducationCheckConsentSignature?: string;
	safeInEducationCheckConsentDate?: string;
	healthRecordCheckConsent?: boolean;
	healthRecordCheckConsentName?: string;
	healthRecordCheckConsentSignature?: string;
	healthRecordCheckConsentDate?: string;
	//criminal record
	criminalRecordCheckConsent?: boolean;
	criminalRecordCheckConsentName?: string;
	criminalRecordCheckConsentDate?: string;
	criminalRecordCheckConsentSignature?: string;
	declarationCheckConsent: boolean;
	declarationCheckConsentName?: string;
	declarationCheckConsentSignature?: string;
	declarationCheckConsentDate?: string;
	confirmed: boolean;
	contractForServiceConsent: boolean;
	contractForServiceName?: string;
	contractForServiceSignature?: string;
	contractForServiceDate?: string;
	assignee?: UserInterface;
	profilePhoto?: string;
	payType: CandidatePay;
	payInterval?: CandidatePaymentIntervals;
	payRate?: string;
	optOut?: boolean;
	_customId?: string;
	_internalId?: string;
	statusChangeDate?: string;
	umbrella?: string;
	payeUmbrella?: string;
	transport?: string;
	importantNotes?: string;
	updatedAt?: Date;
	source?: string;
	sourceOther?: string;
	onlineCheckConsent?: boolean;
	title?: string;
	pronoun?: string;
	teacherPayScale?: string;
	p45?: boolean;
	roleType?: string;
	rtwVerification?: string;
	rtwVerificationNote?: string;
	rtwVerificationStatus?: string;
	desiredLocation?: string;
	complianceUser?: UserInterface;
	desiredLocationMultiple?: string[];
	availabilityLastChecked?: string;
	subsidiarySubject?: string[];
	subjectToNotice?: string;
	employmentPreference?: string[];
	otherNames?: string;
	ltdCompany?: string;
	employmentVerified?: boolean;
	employmentGapsDescription?: string;
	optOut48Hours?: boolean;
	//compliance overrides
	referenceCheckOverride?: boolean;
	declarationCheckOverride?: boolean;
	safeInEducationOverride?: boolean;
	criminalRecordOverride?: boolean;
	contractForServiceOverride?: boolean;
	healthDeclarationOverride?: boolean;
	//rtw dbs
	candidateDBSVerification?: string;
	candidateDBSVerificationNote?: string;
	candidateDBSVerificationStatus?: string;
	//rtw dbsCheck
	candidateDBSCheckVerification?: string;
	candidateDBSCheckVerificationNote?: string;
	candidateDBSCheckVerificationStatus?: string;
	//rtw dbsUpdateService
	candidateDBSUpdateServiceVerificationNote?: string;
	candidateDBSUpdateServiceVerificationStatus?: string;
	//rtw trn
	candidateTRNVerificationNote?: string;
	candidateTRNVerificationStatus?: string;
	//rtw health declartaion
	candidateHealthSafetyVerification?: string;
	candidateHealthSafetyVerificationNote?: string;
	candidateHealthSafetyVerificationStatus?: string;
	candidateSignatures?: CandidateSignatures;
	//rec audit consent
	recAuditConsent?: boolean | null;
	recAuditConsentDate?: string;
	recAuditConsentMethod?: string;

	constructor(data: CandidateInterface) {
		this.forename = data?.forename || "";
		this.surname = data?.surname || "";
		this.previousNames = data?.previousNames || "";
		this.email = data?.email || "";
		this.telephone = data?.telephone || "";
		this.mobile = data?.mobile || "";
		this.occupation = data?.occupation || null;
		this.trnNumber = data?.trnNumber || "";
		this.trnNumberCheckedDate = data?.trnNumberCheckedDate || "";
		this.trnNumberNotes = data?.trnNumberNotes || null;
		this.employmentStartDate = data?.employmentStartDate || null;
		this.ewcConfirmation = data?.ewcConfirmation !== null && data?.ewcConfirmation !== undefined ? data?.ewcConfirmation : null;
		this.homeOfficeShareCode = data?.homeOfficeShareCode || null;
		this.subject = data?.subject || "";
		this.address = data?.address || new AddressModel();
		this.qualifications = data?.qualifications || [];
		this.employment = data?.employment || [];
		this.references = data?.references || [];
		this.previousAgencies = data?.previousAgencies || [];
		this.previousAddresses = data?.previousAddresses || [];
		this.dob = data?.dob || "";
		this.sex = data?.sex || "";
		this.status = data?.status || 1;
		this.onboardingSent = data?.onboardingSent || false;
		this.nextOfKin = data?.nextOfKin || new NextOfKinModel();
		this.criminalRecord = data?.criminalRecord || new CriminalRecordModel();
		this.criminalRecordDeclaration = data?.criminalRecordDeclaration;
		this.safeInEducation = data?.safeInEducation || new SafeInEducationModel();
		this.socialMedia = data?.socialMedia || new SocialMediaModel();
		this.healthRecord = data?.healthRecord || new HealthRecordModel();
		this.healthDeclaration = data?.healthDeclaration;
		this.bankDetails = data?.bankDetails || new BankDetailsModel();
		this.referenceCheckConsent = data?.referenceCheckConsent || false;
		this.referenceCheckConsentName = data?.referenceCheckConsentName || "";
		this.referenceCheckConsentDate = data?.referenceCheckConsentDate || "";
		this.safeInEducationCheckConsent = data?.safeInEducationCheckConsent || false;
		this.safeInEducationCheckConsentName = data?.safeInEducationCheckConsentName || "";
		this.safeInEducationCheckConsentDate = data?.safeInEducationCheckConsentDate || "";
		this.healthRecordCheckConsent = data?.healthRecordCheckConsent || false;
		this.healthRecordCheckConsentName = data?.healthRecordCheckConsentName || "";
		this.healthRecordCheckConsentDate = data?.healthRecordCheckConsentDate || "";
		//criminal record
		this.criminalRecordCheckConsent = data?.criminalRecordCheckConsent || false;
		this.criminalRecordCheckConsentName = data?.criminalRecordCheckConsentName || "";
		this.criminalRecordCheckConsentDate = data?.criminalRecordCheckConsentDate || "";
		this.declarationCheckConsent = data?.declarationCheckConsent || false;
		this.declarationCheckConsentName = data?.declarationCheckConsentName || "";
		this.declarationCheckConsentDate = data?.declarationCheckConsentDate || "";
		this.contractForServiceConsent = data?.contractForServiceConsent || false;
		this.contractForServiceName = data?.contractForServiceName || "";
		this.contractForServiceDate = data?.contractForServiceDate || null;
		this.confirmed = data?.confirmed || false;
		this.assignee = data?.assignee || null;
		this.profilePhoto = data?.profilePhoto;
		this.payType = data?.payType || null;
		this.payInterval = data?.payInterval || CandidatePaymentIntervals.Weekly;
		this.payRate = data?.payRate || "";
		this.optOut = data?.optOut || false;
		this._customId = data?._customId || "";
		this._internalId = data?._internalId || "";
		this.statusChangeDate = data?.statusChangeDate || "";
		this.umbrella = data?.umbrella || null;
		this.payeUmbrella = data?.payeUmbrella || null;
		this.transport = data?.transport || "";
		this.importantNotes = data?.importantNotes || "";
		this.updatedAt = data?.updatedAt || null;
		this.source = data?.source || null;
		this.sourceOther = data?.sourceOther || null;
		this.onlineCheckConsent = data?.onlineCheckConsent !== null && data?.onlineCheckConsent !== undefined ? data?.onlineCheckConsent : null;
		this.title = data?.title || null;
		this.pronoun = data?.pronoun || null;
		this.teacherPayScale = data?.teacherPayScale || null;
		this.p45 = data?.p45 || null;
		this.roleType = data?.roleType || null;
		this.rtwVerification = data?.rtwVerification || "";
		this.rtwVerificationNote = data?.rtwVerificationNote || "";
		this.rtwVerificationStatus = data?.rtwVerificationStatus || null;
		this.desiredLocation = data?.desiredLocation || null;
		this.complianceUser = data?.complianceUser || null;
		this.desiredLocationMultiple = data?.desiredLocationMultiple || [];
		this.availabilityLastChecked = data?.availabilityLastChecked || "";
		this.subsidiarySubject = data?.subsidiarySubject || [];
		this.subjectToNotice = data?.subjectToNotice || "";
		this.employmentPreference = data?.employmentPreference || [];
		this.otherNames = data?.otherNames || "";
		this.ltdCompany = data?.ltdCompany || null;
		this.employmentVerified = data?.employmentVerified !== null && data?.employmentVerified !== undefined ? data?.employmentVerified : null;
		this.employmentGapsDescription = data?.employmentGapsDescription || null;
		//compliance overrides
		this.referenceCheckOverride = data?.referenceCheckOverride || false;
		this.declarationCheckOverride = data?.declarationCheckOverride || false;
		this.safeInEducationOverride = data?.safeInEducationOverride || false;
		this.criminalRecordOverride = data?.criminalRecordOverride || false;
		this.contractForServiceOverride = data?.contractForServiceOverride || false;
		this.healthDeclarationOverride = data?.healthDeclarationOverride || false;
		//rtw dbs
		this.candidateDBSVerification = data?.candidateDBSVerification || null;
		this.candidateDBSVerificationNote = data?.candidateDBSVerificationNote || null;
		this.candidateDBSVerificationStatus = data?.candidateDBSVerificationStatus || null;
		//rtw dbsCheck
		this.candidateDBSCheckVerification = data?.candidateDBSCheckVerification || null;
		this.candidateDBSCheckVerificationNote = data?.candidateDBSCheckVerificationNote || null;
		this.candidateDBSCheckVerificationStatus = data?.candidateDBSCheckVerificationStatus || null;
		//rtw dbsUpdateService
		this.candidateDBSUpdateServiceVerificationNote = data?.candidateDBSUpdateServiceVerificationNote || null;
		this.candidateDBSUpdateServiceVerificationStatus = data?.candidateDBSUpdateServiceVerificationStatus || null;
		//rtw trn
		this.candidateTRNVerificationNote = data?.candidateTRNVerificationNote || null;
		this.candidateTRNVerificationStatus = data?.candidateTRNVerificationStatus || null;
		//rtw health declaration
		this.candidateHealthSafetyVerification = data?.candidateHealthSafetyVerification || null;
		this.candidateHealthSafetyVerificationNote = data?.candidateHealthSafetyVerificationNote || null;
		this.candidateHealthSafetyVerificationStatus = data?.candidateHealthSafetyVerificationStatus || null;
		this.optOut48Hours = data?.optOut48Hours !== null && data?.optOut48Hours !== undefined ? data?.optOut48Hours : null;
		this.candidateSignatures = data?.candidateSignatures || new CandidateSignaturesModel();
		//rec audit consent
		this.recAuditConsent = data?.recAuditConsent ?? null;
		this.recAuditConsentDate = data?.recAuditConsentDate || null;
		this.recAuditConsentMethod = data?.recAuditConsentMethod || null;
	}
}

export class CandidateErrorModel {
	forenameError?: string;
	surnameError?: string;
	previousNamesError?: string;
	otherNamesError?: string;
	desiredLocationError?: string;
	pronounError?: string;
	mobileError?: string;
	desiredLocationMultipleError?: string;
	telephoneError?: string;
	trnNumberError?: string;
	transportError?: string;
	ewcConfirmationError?: string;
	subsidiarySubjectError?: string;
	employmentPreferenceError?: string;
	homeOfficeShareCodeError?: string;
	teacherPayScaleError?: string;
	addressLine1Error?: string;
	postcodeError?: string;
	boroughError?: string;
	qualificationsError?: string;
	emailError?: string;
	statusError?: string;
	dbsHeldError?: string;
	dbsLastCheckedError?: string;
	prohibitionCheckError?: string;
	dbsNumberError?: string;
	convictedOffenceError?: string;
	convictionsError?: string;
	convictionsDescriptionError?: string;
	barredError?: string;
	waiverError?: string;
	ordersYouError?: string;
	ordersChildError?: string;
	cautionedError?: string;
	violentOffenceError?: string;
	anotherCountryError?: string;
	safeInEducationDetailsError?: string;
	healthIssuesError?: string;
	healthIssuesDescriptionError?: string;
	healthDeclarationAnswerError?: Record<string, string>;
	criminalRecordDeclarationAnswerError?: Record<string, string>;
	nextOfKinNameError?: string;
	nextOfKinTelephoneError?: string;
	nextOfKinEmailError?: string;
	abroadError?: string;
	overseasDeclarationExtendedError?: string;
	dbsDeclarationReadError?: string;
	educationalDisciplinaryProceedingsError?: string;
	readKCSIEError?: string;
	prohibitedFromTeachingError?: string;
	knownPoliceError?: string;
	prohibitedFromManagementError?: string;
	criminalOffencesLawError?: string;
	readChildcareActError?: string;
	sourceError?: string;

	constructor(data?: Partial<CandidateErrorInterface>) {
		this.forenameError = data?.forenameError || null;
		this.surnameError = data?.surnameError || null;
		this.previousNamesError = data?.previousNamesError || null;
		this.mobileError = data?.mobileError || null;
		this.qualificationsError = data?.qualificationsError || null;
		this.pronounError = data?.pronounError || null;
		this.desiredLocationMultipleError = data?.desiredLocationMultipleError || null;
		this.desiredLocationError = data?.desiredLocationError || null;
		this.trnNumberError = data?.trnNumberError || null;
		this.telephoneError = data?.telephoneError || null;
		this.otherNamesError = data?.otherNamesError || null;
		this.teacherPayScaleError = data?.teacherPayScaleError || null;
		this.transportError = data?.transportError || null;
		this.homeOfficeShareCodeError = data?.homeOfficeShareCodeError || null;
		this.ewcConfirmationError = data?.ewcConfirmationError || null;
		this.subsidiarySubjectError = data?.subsidiarySubjectError || null;
		this.employmentPreferenceError = data?.employmentPreferenceError || null;
		this.addressLine1Error = data?.addressLine1Error || null;
		this.postcodeError = data?.postcodeError || null;
		this.boroughError = data?.boroughError || null;
		this.emailError = data?.emailError || null;
		this.statusError = data?.statusError || null;
		this.dbsHeldError = data?.dbsHeldError || null;
		this.dbsLastCheckedError = data?.dbsLastCheckedError || null;
		this.prohibitionCheckError = data?.prohibitionCheckError || null;
		this.dbsNumberError = data?.dbsNumberError || null;
		this.convictedOffenceError = data?.convictedOffenceError || null;
		this.convictionsError = data?.convictionsError || null;
		this.convictionsDescriptionError = data?.convictionsDescriptionError || null;
		this.barredError = data?.barredError || null;
		this.waiverError = data?.waiverError || null;
		this.ordersYouError = data?.ordersYouError || null;
		this.ordersChildError = data?.ordersChildError || null;
		this.cautionedError = data?.cautionedError || null;
		this.violentOffenceError = data?.violentOffenceError || null;
		this.anotherCountryError = data?.anotherCountryError || null;
		this.safeInEducationDetailsError = data?.safeInEducationDetailsError || null;
		this.healthIssuesError = data?.healthIssuesError || null;
		this.healthIssuesDescriptionError = data?.healthIssuesDescriptionError || null;
		this.healthDeclarationAnswerError = data?.healthDeclarationAnswerError || null;
		this.criminalRecordDeclarationAnswerError = data?.criminalRecordDeclarationAnswerError || null;
		this.nextOfKinNameError = data?.nextOfKinNameError || null;
		this.nextOfKinTelephoneError = data?.nextOfKinTelephoneError || null;
		this.nextOfKinEmailError = data?.nextOfKinEmailError || null;
		this.abroadError = data?.abroadError || null;
		this.overseasDeclarationExtendedError = data?.overseasDeclarationExtendedError || null;
		this.dbsDeclarationReadError = data?.dbsDeclarationReadError || null;
		this.educationalDisciplinaryProceedingsError = data?.educationalDisciplinaryProceedingsError || null;
		this.readKCSIEError = data?.readKCSIEError || null;
		this.prohibitedFromTeachingError = data?.prohibitedFromTeachingError || null;
		this.knownPoliceError = data?.knownPoliceError || null;
		this.prohibitedFromManagementError = data?.prohibitedFromManagementError || null;
		this.criminalOffencesLawError = data?.criminalOffencesLawError || null;
		this.sourceError = data?.sourceError || null;
		this.readChildcareActError = data?.readChildcareActError || null;
	}
}

export class CandidateOnboardModel {
	forename: string;
	surname: string;
	previousNames?: string;
	email: string;
	occupation?: OccupationTypes;
	trnNumber?: string;
	ewcConfirmation?: boolean;
	homeOfficeShareCode?: string;
	subject?: string;
	telephone?: string;
	mobile?: string;
	address: AddressModel;
	qualifications: any;
	employment: any;
	references: any;
	previousAgencies: PreviousAgencyInterface[];
	previousAddresses: IPreviousAddress[];
	dob?: string;
	sex?: string;
	onboardingSent?: boolean;
	nextOfKin: NextOfKinModel;
	criminalRecord: CriminalRecordModel;
	criminalRecordDeclaration?: CriminalRecordDeclarationSnapshot | CriminalRecordDeclarationSubmission;
	socialMedia: SocialMediaModel;
	safeInEducation: SafeInEducationModel;
	healthRecord: IHealthRecord;
	healthDeclaration?: HealthDeclarationSnapshot | HealthDeclarationSubmission;
	bankDetails: BankDetailsModel;
	referenceCheckConsent: boolean;
	referenceCheckConsentName?: string;
	referenceCheckConsentDate?: string;
	safeInEducationCheckConsent: boolean;
	safeInEducationCheckConsentName?: string;
	safeInEducationCheckConsentDate?: string;
	healthRecordCheckConsent?: boolean;
	healthRecordCheckConsentName?: string;
	healthRecordCheckConsentDate?: string;
	criminalRecordCheckConsent?: boolean;
	criminalRecordCheckConsentName?: string;
	criminalRecordCheckConsentDate?: string;
	declarationCheckConsent: boolean;
	declarationCheckConsentName?: string;
	declarationCheckConsentDate?: string;
	confirmed: boolean;
	contractForServiceConsent: boolean;
	contractForServiceName?: string;
	contractForServiceDate?: string;
	assignee?: UserInterface;
	profilePhoto?: string;
	payType: CandidatePay;
	umbrella?: string;
	_customId?: string;
	_internalId?: string;
	transport?: string;
	onlineCheckConsent?: boolean;
	title?: string;
	pronoun?: string;
	teacherPayScale?: string;
	roleType?: string;
	desiredLocation?: string;
	desiredLocationMultiple?: string[];
	subsidiarySubject?: string[];
	employmentPreference?: string[];
	otherNames?: string;
	experienceSummary?: string;
	employmentGapsDescription?: string;
	optOut48Hours?: boolean;
	candidateSignatures?: CandidateSignatures;
	//rec audit consent
	recAuditConsent?: boolean | null;
	recAuditConsentDate?: string;
	recAuditConsentMethod?: string;

	constructor(data: CandidateInterface & { experienceSummary?: string }) {
		this.forename = data?.forename || "";
		this.surname = data?.surname || "";
		this.previousNames = data?.previousNames || "";
		this.email = data?.email || "";
		this.telephone = data?.telephone || "";
		this.mobile = data?.mobile || "";
		this.occupation = data?.occupation || null;
		this.trnNumber = data?.trnNumber || "";
		this.ewcConfirmation = data?.ewcConfirmation !== null && data?.ewcConfirmation !== undefined ? data?.ewcConfirmation : null;
		this.homeOfficeShareCode = data?.homeOfficeShareCode || null;
		this.subject = data?.subject || "";
		this.address = data?.address || new AddressModel();
		this.qualifications = data?.qualifications || [];
		this.employment = data?.employment || [];
		this.references = data?.references || [];
		this.previousAgencies = data?.previousAgencies || [];
		this.previousAddresses = data?.previousAddresses || [];
		this.dob = data?.dob || "";
		this.sex = data?.sex || "";
		this.onboardingSent = data?.onboardingSent || false;
		this.nextOfKin = data?.nextOfKin || new NextOfKinModel();
		this.criminalRecord = data?.criminalRecord || new CriminalRecordModel();
		this.criminalRecordDeclaration = data?.criminalRecordDeclaration;
		this.safeInEducation = data?.safeInEducation || new SafeInEducationModel();
		this.socialMedia = data?.socialMedia || new SocialMediaModel();
		this.healthRecord = data?.healthRecord || new HealthRecordModel();
		this.healthDeclaration = data?.healthDeclaration;
		this.bankDetails = data?.bankDetails || new BankDetailsModel();
		this.referenceCheckConsent = data?.referenceCheckConsent || false;
		this.referenceCheckConsentName = data?.referenceCheckConsentName || "";
		this.referenceCheckConsentDate = data?.referenceCheckConsentDate || "";
		this.safeInEducationCheckConsent = data?.safeInEducationCheckConsent || false;
		this.safeInEducationCheckConsentName = data?.safeInEducationCheckConsentName || "";
		this.safeInEducationCheckConsentDate = data?.safeInEducationCheckConsentDate || "";
		this.healthRecordCheckConsent = data?.healthRecordCheckConsent || false;
		this.healthRecordCheckConsentName = data?.healthRecordCheckConsentName || "";
		this.healthRecordCheckConsentDate = data?.healthRecordCheckConsentDate || "";
		//criminal record
		this.criminalRecordCheckConsent = data?.criminalRecordCheckConsent || false;
		this.criminalRecordCheckConsentName = data?.criminalRecordCheckConsentName || "";
		this.criminalRecordCheckConsentDate = data?.criminalRecordCheckConsentDate || "";
		this.declarationCheckConsent = data?.declarationCheckConsent || false;
		this.declarationCheckConsentName = data?.declarationCheckConsentName || "";
		this.declarationCheckConsentDate = data?.declarationCheckConsentDate || "";
		this.contractForServiceConsent = data?.contractForServiceConsent || false;
		this.contractForServiceName = data?.contractForServiceName || "";
		this.contractForServiceDate = data?.contractForServiceDate || "";
		this.confirmed = data?.confirmed || false;
		this.assignee = data?.assignee || null;
		this.profilePhoto = data?.profilePhoto;
		this.payType = data?.payType || null;
		this.umbrella = data?.umbrella || null;
		this._customId = data?._customId || "";
		this._internalId = data?._internalId || "";
		this.transport = data?.transport || "";
		this.onlineCheckConsent = data?.onlineCheckConsent !== null && data?.onlineCheckConsent !== undefined ? data?.onlineCheckConsent : null;
		this.title = data?.title || null;
		this.pronoun = data?.pronoun || null;
		this.teacherPayScale = data?.teacherPayScale || null;
		this.roleType = data?.roleType || null;
		this.desiredLocation = data?.desiredLocation || null;
		this.desiredLocationMultiple = data?.desiredLocationMultiple || [];
		this.subsidiarySubject = data?.subsidiarySubject || [];
		this.employmentPreference = data?.employmentPreference || [];
		this.otherNames = data?.otherNames || "";
		this.employmentGapsDescription = data?.employmentGapsDescription || null;
		this.optOut48Hours = data?.optOut48Hours !== null && data?.optOut48Hours !== undefined ? data?.optOut48Hours : null;
		this.candidateSignatures = data?.candidateSignatures || new CandidateSignaturesModel();
		//rec audit consent
		this.recAuditConsent = data?.recAuditConsent ?? null;
		this.recAuditConsentDate = data?.recAuditConsentDate || null;
		this.recAuditConsentMethod = data?.recAuditConsentMethod || null;
		if (data?.experienceSummary) {
			this.experienceSummary = data?.experienceSummary || null;
		}
	}
}

export class CandidateAssigneeModel {
	assignee?: string;

	constructor(data: any = {}) {
		this.assignee = data?.assignee || "";
	}
}

export class CandidateComplianceUserModel {
	complianceUser?: string;

	constructor(data: any = {}) {
		this.complianceUser = data?.complianceUser || "";
	}
}
